import moment from 'moment';
import { toFirstUpperCase, getFormatNumPlus, getValidDate } from '@gp/utils/common';
import { ECHART_COLOR } from '@gp/constants/const';
import { BASE_BAR_SERIES_OPTION } from '@gp/constants/echarts';

const OTHERS_FIELD_NAME = 'others';
const TOTAL_TARGET_FIELD_NAME = 'totalTarget';

const getSortedList = (list?: any[]): any[] => {
  if (!Array.isArray(list) || !list.length) return [];
  const sortedList = [...list];
  sortedList.sort((a, b) => {
    const { text: aText, value: aValue } = a || {};
    const { text: bText, value: bValue } = b || {};
    if (aText?.toLowerCase() === 'others') return 1;
    if (bText?.toLowerCase() === 'others') return -1;
    const aNum = Number.parseFloat(aValue);
    const bNum = Number.parseFloat(bValue);
    if (Number.isNaN(aNum) && Number.isNaN(bNum)) return 0;
    if (Number.isNaN(aNum)) return 1;
    if (Number.isNaN(bNum)) return -1;
    return bNum - aNum;
  });
  return sortedList;
};

const getBarChartOptionData = (data: any): any => {
  const dates: Set<string> = new Set();
  const projectIds: Set<string> = new Set();
  const seriesMap: Map<string, boolean> = new Map();
  const series: IEChartSeriesOption[] = [];
  const sources: {
    date: string;
    [key: string]: string;
  }[] = [];
  const optionData: {
    xAxis: {
      data: string[];
    };
    series: IEChartSeriesOption[];
    dataset: {
      dimensions: string[];
      source: { date: string; [key: string]: string }[];
    };
  } = {
    xAxis: {
      data: [],
    },
    series: [],
    dataset: {
      dimensions: [],
      source: [],
    },
  };

  if (!Array.isArray(data)) return optionData;

  for (const dataItem of data) {
    if (!dataItem?.date) continue;
    const { date, target: totalTarget, project_list: projects } = dataItem;
    const source: { [key: string]: string } = {};
    let index = 0;
    for (const projectData of projects) {
      if (!projectData?.id || !projectData?.name) continue;
      const projectId = projectData?.id;
      const isExistedSerie = seriesMap.get(projectId);
      projectIds.add(projectId);
      if (!isExistedSerie) {
        series.push({
          ...BASE_BAR_SERIES_OPTION,
          type: 'bar',
          name: projectId,
          stack: 'total',
          color: ECHART_COLOR?.[index >= 7 ? index + 1 : index], // 这里确保跳过total的颜色不重复
        });
        seriesMap.set(projectId, true);
      }
      source[projectId] = projectData?.revenue;
      index ++;
    }
    source[TOTAL_TARGET_FIELD_NAME] = totalTarget;
    dates.add(date);
    sources.push({
      date,
      ...source,
    });
  }
  const targetDataList: any[] = sources?.map((t, i) => {
    // 前后两个点没有数据且当前点有数据时，当前数据点显示出来
    const flag = t?.[TOTAL_TARGET_FIELD_NAME] && !sources?.[i - 1]?.[TOTAL_TARGET_FIELD_NAME] && !sources?.[i + 1]?.[TOTAL_TARGET_FIELD_NAME];
    return {
      value: t?.[TOTAL_TARGET_FIELD_NAME] || null,
      itemStyle: {
        opacity: flag ? 1 : 0,
      },
    };
  });
  series.push({
    type: 'line',
    name: TOTAL_TARGET_FIELD_NAME,
    stack: 'line_total',
    color: ECHART_COLOR?.[7],
    symbol: 'circle',
    symbolSize: 6, // 拐点实心点大小
    emphasis: {
      // focus: 'series',
      scale: 1.25,
      itemStyle: {
        opacity: 1,
      },
    },
    data: targetDataList,
  });
  optionData.xAxis.data = Array.from(dates).sort((a, b) =>
    moment(a).isAfter(moment(b)) ? 1 : -1,
  );
  optionData.series = series;
  optionData.dataset.dimensions = [
    'date',
    ...projectIds,
    TOTAL_TARGET_FIELD_NAME,
  ];
  optionData.dataset.source = sources;
  return optionData;
};

const legendFormatter = (data: any, id: string): string => {
  if (id === OTHERS_FIELD_NAME) return 'OTHERS';
  if (id === TOTAL_TARGET_FIELD_NAME) return 'Total Target';
  if (!id) return '-';
  const target = data?.find((item: any) => item?.id === id);
  return target?.name ? toFirstUpperCase(target?.name) : id;
};

const axisLabelOfXAxisFormatter = (name: string): string => {
  return getValidDate(name, 'YYYY-MM');
};

const axisLabelOfYAxisFormatter = (payload: any): string => {
  return getFormatNumPlus(payload, 2, false);
};

const tootipFormatter = (projects: any, data: any, payload: any): string => {
  const categoryFieldName =
    payload?.[0]?.dimensionNames?.[0] || payload?.dimensionNames?.[0];
  const source: { [key: string]: string } =
    payload?.[0]?.data || payload?.[0]?.value || {};
  const date = payload?.[0]?.name || payload?.name;
  const dataTarget = data?.find(
    (item: any) => item?.[categoryFieldName] === date,
  );
  // 汇总revenue
  const dataTotalRevenue = dataTarget?.total_revenue;
  // 汇总target
  const dataTotalTarget = dataTarget?.target;
  // 系列数据
  const list = Object.entries(source)
    .filter(
      ([key]) => ![categoryFieldName, TOTAL_TARGET_FIELD_NAME].includes(key),
    )
    .reduce<{ text: string; value: string }[]>((current, next) => {
      const [key, value] = next || [];
      return [...current, { text: key, value }];
    }, []);

  const dateHTMLStr = getValidDate(date, 'YYYY-MM');

  const listHTMLStr = getSortedList(list)
    .map((item, index) => {
      const text = legendFormatter(projects, item?.text) || '-';
      return `
      <li class="dbg-studio-overall-details-projects-live-bar-chart-tooltip-details-item">
        <div>
          <span
            class="dbg-studio-overall-details-projects-live-bar-chart-tooltip-dot-icon"
            style="background-color: ${ECHART_COLOR?.[index >= 7 ? index + 1 : index]};"
          ></span>
          <span class="body-text">${text}</span>
        </div>
        <div class="body-num">${getFormatNumPlus(item?.value)}</div>
      </li>
    `;
    })
    .join('');

  return `
  <div class="dbg-studio-overall-details-projects-live-bar-chart-tooltip">
    <div class="dbg-studio-overall-details-projects-live-bar-chart-tooltip-title">
      <div>${dateHTMLStr}</div>
      <div style="margin-left: 60px;">Revenue</div>
    </div>
    <div class="dbg-studio-overall-details-projects-live-bar-chart-tooltip-topbar">
      <div class="dbg-studio-overall-details-projects-live-bar-chart-tooltip-topbar-item">
        <div>
          <span
            class="dbg-studio-overall-details-projects-live-bar-chart-tooltip-dot-icon"
            style="background-color: ${ECHART_COLOR?.[7]};"
          ></span>
          <span class="body-text">Total Target</span>
        </div>
        <div class="body-num">${getFormatNumPlus(dataTotalTarget)}</div>
      </div>
      <div class="dbg-studio-overall-details-projects-live-bar-chart-tooltip-topbar-divider"></div>
      <div class="dbg-studio-overall-details-projects-live-bar-chart-tooltip-topbar-item">
        <div>
          <span class="body-text">Total Revenue</span>
        </div>
        <div class="body-num">${getFormatNumPlus(dataTotalRevenue)}</div>
      </div>
    </div>
    <div class="dbg-studio-overall-details-projects-live-bar-chart-tooltip-details-container">
      <ul class="dbg-studio-overall-details-projects-live-bar-chart-tooltip-details-list">${listHTMLStr}</ul>
    </div>
  </div>
  `;
};

export {
  getBarChartOptionData,
  legendFormatter,
  axisLabelOfXAxisFormatter,
  axisLabelOfYAxisFormatter,
  tootipFormatter,
};
